const ITEM_ACCESS_UUIDS_KEY = 'itemAccessUuids';

interface ItemAccessUuidMap {
  [itemUuid: string]: string;
}

export const getAccessUuidForItem = (itemUuid: string): string | null => {
  try {
    const storedData = localStorage.getItem(ITEM_ACCESS_UUIDS_KEY);
    if (!storedData) return null;
    
    const itemMap: ItemAccessUuidMap = JSON.parse(storedData);
    return itemMap[itemUuid] || null;
  } catch (error) {
    console.error('Error reading item access UUIDs from localStorage:', error);
    return null;
  }
};

export const setAccessUuidForItem = (itemUuid: string, accessUuid: string): void => {
  try {
    const storedData = localStorage.getItem(ITEM_ACCESS_UUIDS_KEY);
    let itemMap: ItemAccessUuidMap = storedData ? JSON.parse(storedData) : {};
    
    // Check if we have more than 100 entries and clear if so
    if (Object.keys(itemMap).length > 100) {
      console.log('Access UUID storage exceeded 100 entries, clearing all stored UUIDs');
      itemMap = {};
    }
    
    itemMap[itemUuid] = accessUuid;
    
    localStorage.setItem(ITEM_ACCESS_UUIDS_KEY, JSON.stringify(itemMap));
  } catch (error) {
    console.error('Error saving item access UUID to localStorage:', error);
  }
};

export const removeAccessUuidForItem = (itemUuid: string): void => {
  try {
    const storedData = localStorage.getItem(ITEM_ACCESS_UUIDS_KEY);
    if (!storedData) return;
    
    const itemMap: ItemAccessUuidMap = JSON.parse(storedData);
    delete itemMap[itemUuid];
    
    localStorage.setItem(ITEM_ACCESS_UUIDS_KEY, JSON.stringify(itemMap));
  } catch (error) {
    console.error('Error removing item access UUID from localStorage:', error);
  }
};

export const getAllItemAccessUuids = (): ItemAccessUuidMap => {
  try {
    const storedData = localStorage.getItem(ITEM_ACCESS_UUIDS_KEY);
    return storedData ? JSON.parse(storedData) : {};
  } catch (error) {
    console.error('Error reading all item access UUIDs from localStorage:', error);
    return {};
  }
};

// Hotel sorting utility
import { VoucherHotel } from 'services/ItineraryManagerApi/types';

/**
 * Sorts hotels by their earliest accommodation start date, with alphabetical fallback
 * @param hotels Array of VoucherHotel objects to sort
 * @returns Sorted array of hotels (earliest date first, then alphabetical by name)
 */
export const sortHotelsByEarliestDate = (hotels: VoucherHotel[]): VoucherHotel[] => {
  return hotels.slice().sort((a, b) => {
    // Get earliest accommodation start date for each hotel
    const getEarliestDate = (hotel: VoucherHotel): Date => {
      if (!hotel.accommodations.length) return new Date('9999-12-31'); // Far future for hotels with no accommodations
      
      const startDates = hotel.accommodations
        .map(acc => new Date(acc.startDate))
        .filter(date => !isNaN(date.getTime())); // Filter out invalid dates
      
      return startDates.length ? new Date(Math.min(...startDates.map(d => d.getTime()))) : new Date('9999-12-31');
    };
    
    const dateA = getEarliestDate(a);
    const dateB = getEarliestDate(b);
    
    // Primary sort: by earliest date
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA.getTime() - dateB.getTime();
    }
    
    // Secondary sort: alphabetical by hotel name
    return a.name.localeCompare(b.name);
  });
};